/**
 * Dynamic service name and description template.
 * https://stamps.atlassian.net/browse/WEBAPPS-30894
 */

import PackageType from './PackageType.enum';
import ServiceType from './ServiceType.enum';

export default Object.freeze({
	// USPS domestic services
	[ServiceType.UspsFirstClassMail]: {
		serviceTypeDescription:
			'[[serviceName]] shipments arrive in 1 to 3 days. The best-priced USPS service for items [[maxWeight]] and under.',
		packages: {
			[PackageType.LargeEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] using your own large envelope or flat with pricing that varies by weight, not distance.',
			},
			[PackageType.Package]: {
				packageDescription:
					'Ship up to [[maxWeight]] using your own box or thick envelope with pricing that varies by weight and distance.',
			},
		},
	},
	[ServiceType.UspsPriorityMail]: {
		serviceTypeDescription: '[[serviceName]] shipments arrive in 1 to 3 days.',
		packages: {
			[PackageType.Package]: {
				packageDescription:
					'Ship up to [[maxWeight]] using any box with pricing that varies by weight and distance. The USPS provides free packaging for Priority Mail which can be ordered from Stamps.com.',
			},
			[PackageType.UspsFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] to anywhere in the U.S. for one low rate.  You must use a USPS-provided envelope which can be ordered for free from Stamps.com.<br /><br />Available envelope sizes:<br />Standard: 9 1/2" x 12 1/2"<br />Gift Card: 7" x 10"<br />Windowed: 5" x 10"<br />Small: 6" x 10"',
			},
			[PackageType.UspsPaddedFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] to anywhere in the U.S. for one low rate.  You must use the USPS-provided 9 1/2" x 12 1/2" padded envelope which can be ordered for free from Stamps.com.',
			},
			[PackageType.UspsLegalFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] to anywhere in the U.S. for one low rate.  You must use the USPS-provided 9 1/2" x 15" envelope which can be ordered for free from Stamps.com.',
			},
			[PackageType.UspsSmallFlatRateBox]: {
				packageDescription:
					'Ship up to [[maxWeight]] to anywhere in the U.S. for one low rate.  You must use the USPS-provided 8 11/16" x 5 7/16" x 1 3/4" box which can be ordered for free from Stamps.com.',
			},
			[PackageType.UspsMediumFlatRateBox]: {
				packageDescription:
					'Ship up to [[maxWeight]] to anywhere in the U.S. for one low rate.  You must use a USPS-provided box which can be ordered for free from Stamps.com.<br /><br />Available box sizes:<br />Top Loading: 11 1/4" x 8 3/4" x 6"<br />Side Loading: 14" x 12"x 3 1/2"',
			},
			[PackageType.UspsLargeFlatRateBox]: {
				packageDescription:
					'Ship up to [[maxWeight]] to anywhere in the U.S. for one low rate.  You must use a USPS-provided box which can be ordered for free from Stamps.com.<br /><br />Available box sizes:<br />Standard: 12 1/4"x 12 1/4" x 6"<br />Board Game: 24 1/16" x 11 7/8" x 3 1/8"',
			},
		},
	},
	[ServiceType.UspsPriorityMailExpress]: {
		serviceTypeDescription:
			'[[serviceName]] is the fastest USPS service with guaranteed next-day service to most locations or your money back.',
		packages: {
			[PackageType.Package]: {
				packageDescription:
					'Ship up to [[maxWeight]] using any box with pricing that varies by weight, distance, and dimensions.',
			},
			[PackageType.UspsFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] to anywhere in the U.S. for one low rate.  You must use the 9 1/2" x 12 1/2" USPS-provided envelope which can be ordered for free from Stamps.com.',
			},
			[PackageType.UspsPaddedFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] to anywhere in the U.S. for one low rate.  You must use the USPS-provided 9 1/2" x 12 1/2" padded envelope which can be ordered for free from Stamps.com.',
			},
			[PackageType.UspsLegalFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] to anywhere in the U.S. for one low rate.  You must use the USPS-provided 9 1/2" x 15" envelope which can be ordered for free from Stamps.com.',
			},
		},
	},
	[ServiceType.UspsMediaMail]: {
		serviceTypeDescription:
			'[[serviceName]] shipments arrive in 2 to 9 days. Ideal for heavy items when cost is more important than speed.',
		packages: {
			[PackageType.Package]: {
				packageDescription:
					'Ship up to [[maxWeight]] using your own thick envelope or box with pricing that varies by weight, not distance.',
			},
		},
	},
	[ServiceType.UspsParcelSelect]: {
		serviceTypeDescription:
			'[[serviceName]] shipments arrive in 2 to 9 days. This service is only available when sending books, sound recordings, recorded video, printed music, and recorded computer readable media (such as CDs, DVDs).',
		packages: {
			[PackageType.Package]: {
				packageDescription:
					'Ship up to [[maxWeight]] using any box no larger than one cubic foot with pricing that varies by weight and distance. ',
			},
		},
	},
	[ServiceType.UspsFirstClassMailIntl]: {
		serviceTypeDescription:
			'[[serviceName]] is the most economical way to send letters, large envelopes, and small packages.',
		packages: {
			[PackageType.LargeEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] for items other than documents (not available to certain account types), or [[maxWeight]] for documents, using your own large envelope or flat with pricing that varies by weight and country.',
			},
			[PackageType.Package]: {
				packageDescription:
					'Ship up to [[maxWeight]] and $400 worth of merchandise using your own box or thick envelope with pricing that varies by weight and country.',
			},
		},
	},
	[ServiceType.UspsPriorityMailIntl]: {
		serviceTypeDescription:
			'[[serviceName]] is a fast, affordable way to ship internationally.',
		packages: {
			[PackageType.Package]: {
				packageDescription:
					'Ship up to [[maxWeight]] using any large or thick envelope or box with pricing that varies by weight and country.  The USPS provides free packaging for Priority Mail which can be ordered from Stamps.com.',
			},
			[PackageType.UspsFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] with pricing that varies by country, not weight.  You must use a USPS-provided envelope which can be ordered for free from Stamps.com.<br /><br />Available envelope sizes:<br />Standard: 9 1/2" x 12 1/2"<br />Gift Card: 7" x 10"<br />Windowed: 5" x 10"<br />Small: 6" x 10"',
			},
			[PackageType.UspsPaddedFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] with pricing that varies by country, not weight.  You must use the USPS-provided 9 1/2" x 12 1/2" padded envelope which can be ordered for free from Stamps.com.',
			},
			[PackageType.UspsLegalFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] with pricing that varies by country, not weight.  You must use the USPS-provided 9 1/2" x 15" envelope which can be ordered for free from Stamps.com.',
			},
			[PackageType.UspsSmallFlatRateBox]: {
				packageDescription:
					'Ship up to [[maxWeight]] with pricing that varies by country, not weight.  You must use the USPS-provided 8 11/16" x 5 7/16" x 1 3/4" box which can be ordered for free from Stamps.com.',
			},
			[PackageType.UspsMediumFlatRateBox]: {
				packageDescription:
					'Ship up to [[maxWeight]] with pricing that varies by country, not weight.  You must use a USPS-provided box which can be ordered for free from Stamps.com.<br /><br />Available box sizes:<br />Top Loading: 11 1/4" x 8 3/4" x 6"<br />Side Loading: 14" x 12"x 3 1/2"',
			},
			[PackageType.UspsLargeFlatRateBox]: {
				packageDescription:
					'Ship up to [[maxWeight]] with pricing that varies by country, not weight.  You must use a USPS-provided box which can be ordered for free from Stamps.com.<br /><br />Available box sizes:<br />Top Loading: 12 1/4"x 12 1/4" x 6"<br />Side Loading: 24 1/16" x 11 7/8" x 3 1/8"',
			},
		},
	},
	[ServiceType.UspsPriorityMailExpressIntl]: {
		serviceTypeDescription:
			'[[serviceName]] offers guaranteed delivery dates to Great Britain, Northern Ireland, Australia, France, China, Hong Kong, Japan, Republic of Korea (South Korea), Singapore, Spain, as well as highly reliable service to over 170 other countries.',
		packages: {
			[PackageType.Package]: {
				packageDescription:
					'Ship up to [[maxWeight]] using any thick envelope or box with pricing that varies by weight and country. The USPS provides free packaging for Priority Mail Express which can be ordered from Stamps.com.',
			},
			[PackageType.UspsFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] with pricing that varies by country, not weight.  You must use the 9 1/2" x 12 1/2" USPS-provided envelope which can be ordered for free from Stamps.com.',
			},
			[PackageType.UspsPaddedFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] with pricing that varies by country, not weight.  You must use the 9 1/2" x 12 1/2" USPS-provided padded envelope which can be ordered for free from Stamps.com.',
			},
			[PackageType.UspsLegalFlatRateEnvelope]: {
				packageDescription:
					'Ship up to [[maxWeight]] with pricing that varies by country, not weight.  You must use the 9 1/2" x 15" USPS-provided envelopes which can be ordered for free from Stamps.com.',
			},
		},
	},
	[ServiceType.DhlExpressWorldwide]: {
		packages: {
			[PackageType.Package]: {
				packageDescription:
					'Ship up to [[maxWeight]] using your own packaging, with pricing that varies by weight and country.',
			},
		},
	},
});
